/* eslint-disable */
const mapboxgl = require('mapbox-gl/dist/mapbox-gl-csp.js');
require('mapbox-gl/dist/mapbox-gl.css');

// Load the CSP worker via a blob URL from the raw source text
const workerSource = require('!!raw-loader!mapbox-gl/dist/mapbox-gl-csp-worker.js');
const workerBlob = new Blob([workerSource.default || workerSource], { type: 'text/javascript' });
const workerUrl = URL.createObjectURL(workerBlob);
mapboxgl.workerUrl = workerUrl;

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

const SEGMENT_COLORS = {
  icp_large: '#3cceb7',
  icp_medium: '#2948a0',
  growth: '#f1b241',
  enterprise: '#ef3dc0',
  provider: '#707f9c'
};

function buildGeoJSON(data) {
  return {
    type: 'FeatureCollection',
    features: data.map(org => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [org.lng, org.lat]
      },
      properties: {
        id: org.id,
        name: org.name,
        segment: org.segment,
        employee_count: org.employee_count || 0,
        therapist_count: org.therapist_count || 0,
        disciplines: (org.disciplines || []).join(', '),
        state: org.state || ''
      }
    }))
  };
}

function matchesDiscipline(org, disciplineId) {
  if (!disciplineId) return true;
  return (org.discipline_ids || []).includes(disciplineId);
}

function filterData(allData, filters) {
  return allData.filter(org => {
    if (filters.state && org.state !== filters.state) return false;
    if (filters.segment && org.segment !== filters.segment) return false;
    if (filters.discipline && !matchesDiscipline(org, filters.discipline)) return false;
    if (filters.q && !org.name.toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  });
}

export default function createMap(container, dataUrl, initialFilters, initialSelectedOrgId) {
  const tokenMeta = document.querySelector('meta[name="mapbox-token"]');
  const token = tokenMeta ? tokenMeta.content : '';

  if (!token) {
    container.innerHTML = '<div class="d-flex align-items-center justify-content-center h-100 text-muted"><p>Mapbox access token not configured. Add <code>mapbox.access_token</code> to Rails credentials (or set the <code>MAPBOX_ACCESS_TOKEN</code> env var) and restart the server.</p></div>';
    return { destroy() {}, applyFilters() {} };
  }

  mapboxgl.accessToken = token;

  const map = new mapboxgl.Map({
    container: container,
    style: 'mapbox://styles/mapbox/light-v11',
    center: [-98.5795, 39.8283],
    zoom: 3.5,
    attributionControl: true
  });

  map.addControl(new mapboxgl.NavigationControl(), 'top-left');

  let allData = [];
  let sourceLoaded = false;
  let currentFilters = initialFilters || {};

  function applyFilters(filters) {
    currentFilters = filters;
    if (!sourceLoaded) return;

    const filtered = filterData(allData, filters);
    const geojson = buildGeoJSON(filtered);
    const source = map.getSource('organizations');
    if (source) {
      source.setData(geojson);
    }
  }

  map.on('load', async () => {
    try {
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error(`Failed to load map data: ${response.status}`);
      const data = await response.json();
      allData = data;

      if (!data || data.length === 0) {
        container.querySelector('.mapboxgl-canvas-container').insertAdjacentHTML(
          'beforeend',
          '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10; background: white; padding: 16px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);"><p class="mb-0 text-muted">No geocoded locations available. Addresses need latitude/longitude data.</p></div>'
        );
        return;
      }

      const geojson = buildGeoJSON(data);

      map.addSource('organizations', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });

      sourceLoaded = true;

      // Cluster circles
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'organizations',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#3cceb7', 5, '#2948a0', 10, '#f1b241', 25, '#ef3dc0'
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            18, 5, 22, 10, 28, 25, 35
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Cluster count labels
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'organizations',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Individual points
      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'organizations',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match', ['get', 'segment'],
            'icp_large', SEGMENT_COLORS.icp_large,
            'icp_medium', SEGMENT_COLORS.icp_medium,
            'growth', SEGMENT_COLORS.growth,
            'enterprise', SEGMENT_COLORS.enterprise,
            'provider', SEGMENT_COLORS.provider,
            SEGMENT_COLORS.provider
          ],
          'circle-radius': 7,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Click cluster -> zoom
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;
        map.getSource('organizations').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
        });
      });

      // Click point -> show details
      map.on('click', 'unclustered-point', (e) => {
        const feature = e.features[0];
        new mapboxgl.Popup({ offset: 15, maxWidth: '250px' })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(`
            <strong>${escapeHtml(feature.properties.name)}</strong><br>
            <span style="color: ${SEGMENT_COLORS[feature.properties.segment] || '#707f9c'}">
              ${escapeHtml((feature.properties.segment || '').replace('_', ' ').toUpperCase())}
            </span><br>
            <small>${escapeHtml(feature.properties.state)} &middot; ${feature.properties.therapist_count} therapists</small>
            ${feature.properties.disciplines ? `<br><small>${escapeHtml(feature.properties.disciplines)}</small>` : ''}
          `)
          .addTo(map);

        const customEvent = new CustomEvent('icp-universe-map:org-clicked', {
          bubbles: true,
          detail: { orgId: feature.properties.id }
        });
        container.dispatchEvent(customEvent);
      });

      // Cursor changes
      map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'unclustered-point', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'unclustered-point', () => { map.getCanvas().style.cursor = ''; });

      // Apply initial filters if any are set
      if (Object.values(currentFilters).some(v => v)) {
        applyFilters(currentFilters);
      }

      // Apply initial org selection — fly to the selected org and show popup
      if (initialSelectedOrgId) {
        selectOrg(initialSelectedOrgId);
      }

    } catch (error) {
      console.error('Failed to load map data:', error);
    }
  });

  function selectOrg(orgId) {
    const org = allData.find(o => o.id === orgId);
    if (!org || !org.lat || !org.lng) return;

    map.flyTo({ center: [org.lng, org.lat], zoom: 8, duration: 1000 });

    // Show popup for the selected org
    new mapboxgl.Popup({ offset: 15, maxWidth: '250px' })
      .setLngLat([org.lng, org.lat])
      .setHTML(`
        <strong>${escapeHtml(org.name)}</strong><br>
        <span style="color: ${SEGMENT_COLORS[org.segment] || '#707f9c'}">
          ${escapeHtml((org.segment || '').replace(/_/g, ' ').toUpperCase())}
        </span><br>
        <small>${escapeHtml(org.state || '')} &middot; ${org.therapist_count || 0} therapists</small>
      `)
      .addTo(map);
  }

  return {
    applyFilters,
    selectOrg,
    resize() {
      if (map) map.resize();
    },
    destroy() {
      if (map) map.remove();
    }
  };
}
